import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Globe } from "lucide-react";
import { useI18n } from "@/lib/i18n";

/**
 * Compact top-nav language switcher.
 * - Auto-detects on first visit from navigator.language
 * - Persists to localStorage and broadcasts adn-lang-change
 * - Syncs the chat language preference too
 */
export default function LangSwitcher({ darkTheme = true }) {
    const { lang, setLang: setI18nLang, LANGS } = useI18n();
    const [open, setOpen] = useState(false);
    const [pos, setPos] = useState(null);
    const btnRef = useRef(null);
    const menuRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        if (btnRef.current) {
            const r = btnRef.current.getBoundingClientRect();
            setPos({ top: r.bottom + 6, right: Math.max(8, window.innerWidth - r.right) });
        }
        const onClick = (e) => {
            const inBtn = btnRef.current && btnRef.current.contains(e.target);
            const inMenu = menuRef.current && menuRef.current.contains(e.target);
            if (!inBtn && !inMenu) setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const current = LANGS.find((l) => l.code === lang) || LANGS[0];

    return (
        <div className="relative" data-testid="lang-switcher">
            <button
                ref={btnRef}
                onClick={() => setOpen((v) => !v)}
                aria-label={`Language: ${current.name}`}
                aria-expanded={open}
                data-testid="lang-switcher-btn"
                className={`inline-flex items-center gap-1.5 px-2 py-1 rounded-sm text-xs font-bold uppercase tracking-widest transition-colors ${
                    darkTheme
                        ? "text-white/75 hover:text-[#D4AF37]"
                        : "text-[#0A1128]/75 hover:text-[#9B1B30]"
                }`}
            >
                <Globe className="w-3.5 h-3.5" />
                {current.short}
            </button>
            {open && pos && createPortal(
                <div
                    ref={menuRef}
                    data-testid="lang-switcher-menu"
                    style={{ position: "fixed", top: pos.top, right: pos.right, width: 224 }}
                    className="z-[10000] bg-white border border-black/10 rounded-md shadow-2xl py-1"
                >
                    {LANGS.map((l) => (
                        <button
                            key={l.code}
                            onClick={() => { setI18nLang(l.code); setOpen(false); }}
                            data-testid={`lang-switcher-${l.code}`}
                            className={`w-full text-left flex items-center justify-between gap-3 px-3 py-2 text-sm transition-colors ${
                                l.code === lang ? "bg-[#0A1128] text-white" : "text-[#1A1D24] hover:bg-[#FDFBF7]"
                            }`}
                        >
                            <span className="font-medium">{l.name}</span>
                            <span className={`text-[10px] font-bold uppercase tracking-widest ${l.code === lang ? "text-[#D4AF37]" : "text-[#1A1D24]/50"}`}>
                                {l.short}
                            </span>
                        </button>
                    ))}
                </div>,
                document.body
            )}
        </div>
    );
}
